import { isPointInPolygon } from "../../selectionAreas/isPointInPolygon";
import type { SelectionAreaPoint } from "../../types";
import type { BoundedRoom, FindOpeningRooms } from "./types";

const NO_ROOMS: [null, null] = [null, null];

// The box check first; the ray cast runs only for the few rooms around the point.
const containsPoint = (
  { room, bounds }: BoundedRoom,
  point: SelectionAreaPoint,
) =>
  point.x >= bounds.minX &&
  point.x <= bounds.maxX &&
  point.y >= bounds.minY &&
  point.y <= bounds.maxY &&
  isPointInPolygon({ point, ring: room.points });

// Steps away from the opening's midpoint, perpendicular to it, until a room is under the test point.
const findRoomBeside = (
  midpoint: SelectionAreaPoint,
  normal: SelectionAreaPoint,
  rooms: BoundedRoom[],
  roomSearchStepsPt: number[],
): string | null => {
  for (const distance of roomSearchStepsPt) {
    const testPoint = {
      x: midpoint.x + normal.x * distance,
      y: midpoint.y + normal.y * distance,
    };
    const found = rooms.find((candidate) =>
      containsPoint(candidate, testPoint),
    );
    if (found) return found.room.name;
  }
  return null;
};

export const findOpeningRooms: FindOpeningRooms = ({
  points,
  rooms,
  roomSearchStepsPt,
}) => {
  if (points.length !== 2) return NO_ROOMS;
  const [start, end] = points;
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  if (length === 0) return NO_ROOMS;

  const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const normal = {
    x: -(end.y - start.y) / length,
    y: (end.x - start.x) / length,
  };
  const mirrored = { x: -normal.x, y: -normal.y };

  return [
    findRoomBeside(midpoint, normal, rooms, roomSearchStepsPt),
    findRoomBeside(midpoint, mirrored, rooms, roomSearchStepsPt),
  ];
};
